import React, { useState, useEffect } from "react";
import {
  Box, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Container, Typography, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Stack, IconButton, Tooltip, TablePagination,
  Grid, Divider,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { collection, onSnapshot, query, orderBy, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../database/db";
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PLAN_COLORS = {
  premium: "success",
  pro: "primary",
  basic: "info",
  trial: "warning",
  free: "default",
};

export default function SubscriptionManagement() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ open: false, sub: null, reason: "" });
  const [refundDialog, setRefundDialog] = useState({ open: false, sub: null, amount: "" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [planFilter, setPlanFilter] = useState("all");

  useEffect(() => {
    const q = query(collection(db, "Subscriptions"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setSubs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const plans = ["all", ...new Set(subs.map((s) => s.plan || "free"))];
  const filtered = planFilter === "all" ? subs : subs.filter((s) => (s.plan || "free") === planFilter);
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const totalRevenue = subs.reduce((s, sub) => s + (sub.amount || 0), 0);
  const active = subs.filter((s) => s.status === "active").length;
  const planBreakdown = subs.reduce((acc, s) => {
    const plan = s.plan || "free";
    acc[plan] = (acc[plan] || 0) + 1;
    return acc;
  }, {});

  const handleCancel = async () => {
    try {
      await updateDoc(doc(db, "Subscriptions", cancelDialog.sub.id), {
        status: "cancelled",
        cancelledAt: serverTimestamp(),
        cancelReason: cancelDialog.reason,
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "subscription-cancel",
        targetName: cancelDialog.sub.userEmail || cancelDialog.sub.userId,
        details: { plan: cancelDialog.sub.plan, reason: cancelDialog.reason },
        timestamp: serverTimestamp(),
      });
      toast.success("Subscription cancelled");
      setCancelDialog({ open: false, sub: null, reason: "" });
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const handleRefund = async () => {
    const amt = parseFloat(refundDialog.amount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await addDoc(collection(db, "Refunds"), {
        subscriptionId: refundDialog.sub.id,
        userId: refundDialog.sub.userId,
        amount: amt,
        status: "pending",
        requestedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "AuditLog"), {
        action: "subscription-refund",
        targetName: refundDialog.sub.userEmail || refundDialog.sub.userId,
        details: { amount: amt, plan: refundDialog.sub.plan },
        timestamp: serverTimestamp(),
      });
      toast.success(`Refund of $${amt} queued`);
      setRefundDialog({ open: false, sub: null, amount: "" });
    } catch (err) {
      toast.error("Failed: " + err.message);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    return (ts.toDate ? ts.toDate() : new Date(ts)).toLocaleDateString();
  };

  return (
    <Page title="Subscriptions | Fire Investing Admin">
      <ToastContainer position="top-right" />
      <Container maxWidth="xl">
        <Box sx={{ pb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Subscription Management</Typography>
          <Typography variant="body2" color="text.secondary">View, cancel, and refund user subscriptions</Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#3dc10e" }}>{active}</Typography>
              <Typography variant="caption" color="text.secondary">Active Subscriptions</Typography>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#f28c3e" }}>${totalRevenue.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
            </Card>
          </Grid>
          {Object.entries(planBreakdown).slice(0, 2).map(([plan, count]) => (
            <Grid item xs={6} md={3} key={plan}>
              <Card sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{count}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>{plan} plan</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Plan filter chips */}
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
          {plans.map((p) => (
            <Chip key={p} label={p === "all" ? "All Plans" : p} size="small"
              variant={planFilter === p ? "filled" : "outlined"}
              color={planFilter === p ? "primary" : "default"}
              onClick={() => { setPlanFilter(p); setPage(0); }}
              sx={{ textTransform: "capitalize" }} />
          ))}
        </Stack>

        <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          <Scrollbar>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>User</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Renews / Ended</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((sub) => {
                    const isExpanded = expandedRow === sub.id;
                    const isActive = sub.status === "active";
                    return (
                      <React.Fragment key={sub.id}>
                        <TableRow hover onClick={() => setExpandedRow(isExpanded ? null : sub.id)}
                          sx={{ cursor: "pointer", bgcolor: isExpanded ? "rgba(61,193,14,0.08)" : "inherit" }}>
                          <TableCell>
                            <IconButton size="small">
                              <Icon icon={isExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} />
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{sub.userEmail || sub.userId || "—"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={sub.plan || "free"} size="small"
                              color={PLAN_COLORS[sub.plan] || "default"} sx={{ textTransform: "capitalize" }} />
                          </TableCell>
                          <TableCell>${(sub.amount || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip label={sub.status || "unknown"} size="small"
                              color={isActive ? "success" : sub.status === "cancelled" ? "error" : "default"}
                              sx={{ textTransform: "capitalize" }} />
                          </TableCell>
                          <TableCell>{formatTime(sub.createdAt)}</TableCell>
                          <TableCell>{formatTime(sub.renewsAt || sub.endedAt || sub.cancelledAt)}</TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              {isActive && (
                                <Tooltip title="Cancel subscription">
                                  <IconButton size="small" color="error"
                                    onClick={() => setCancelDialog({ open: true, sub, reason: "" })}>
                                    <Icon icon="eva:slash-fill" width={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Issue refund">
                                <IconButton size="small" color="warning"
                                  onClick={() => setRefundDialog({ open: true, sub, amount: String(sub.amount || "") })}>
                                  <Icon icon="eva:refresh-fill" width={16} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ bgcolor: "background.default", p: 2 }}>
                              <Grid container spacing={2}>
                                {[
                                  ["Subscription ID", sub.id],
                                  ["User ID", sub.userId],
                                  ["Platform", sub.platform || "—"],
                                  ["Billing Cycle", sub.billingCycle || "monthly"],
                                  ["Provider", sub.provider || "—"],
                                  ["Transaction ID", sub.transactionId || "—"],
                                  ["Cancel Reason", sub.cancelReason || "—"],
                                ].map(([label, val]) => (
                                  <Grid item xs={12} sm={6} md={4} key={label}>
                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>{val}</Typography>
                                  </Grid>
                                ))}
                              </Grid>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {paginated.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          {loading ? "Loading..." : "No subscriptions found"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination rowsPerPageOptions={[15, 25, 50]} component="div"
            count={filtered.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </Card>
      </Container>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, sub: null, reason: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Cancel <strong>{cancelDialog.sub?.userEmail}</strong>'s {cancelDialog.sub?.plan} plan?
          </Typography>
          <TextField fullWidth label="Reason (optional)" value={cancelDialog.reason}
            onChange={(e) => setCancelDialog((p) => ({ ...p, reason: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, sub: null, reason: "" })}>Back</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>Cancel Subscription</Button>
        </DialogActions>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onClose={() => setRefundDialog({ open: false, sub: null, amount: "" })} maxWidth="xs" fullWidth>
        <DialogTitle>Issue Refund</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Refund amount for <strong>{refundDialog.sub?.userEmail}</strong>
          </Typography>
          <TextField fullWidth label="Refund Amount ($)" type="number" value={refundDialog.amount}
            onChange={(e) => setRefundDialog((p) => ({ ...p, amount: e.target.value }))} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            This queues the refund in Firestore. Process via your payment provider.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialog({ open: false, sub: null, amount: "" })}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRefund}>Queue Refund</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
